import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useUserStore } from "@/stores/modules/user";
import { useAuthStore } from "@/stores/modules/auth";
import { AUTHENTICATION_URL, HOME_URL, LOGIN_URL, NO_PERMISSION_URL, ROUTER_WHITE_LIST } from "@/config";
import { staticRouter, errorRouter } from "@/routers/staticRouter";
import { Router } from "vue-router";
import { useUserPath } from "@/stores/modules/path";
import myTaskRouter from "./modules/myTask";
import dataAnalysisRouter from "./modules/dataAnalysis";
import priceMonitorRouter from "./modules/priceMonitor";
import aiRiskControlRouter from "./modules/aiRiskControl";
import manageCenterRouter from "./modules/manageCenterRouter";
import lianYunUser from "@/routers/modules/lianYunUser";
import messageNotify from "@/routers/modules/messageNotify";
import { purchaseSalesInventoryRouters } from "@/routers/purchaseSalesInventory";
import warehouseSupervisionRouters from "@/routers/warehouseSupervision";
import authenticationRouter from "@/routers/modules/authenticationRouter.ts";
import { ref } from "vue";
import { getUserInfo } from "@/utils/login.ts";
import warehouseManagementRouters from "@/routers/warehouseManagement";

const routerMode = createWebHistory(import.meta.env.VITE_PUBLIC_PATH);
/**
 * @description 📚 路由参数配置简介
 * @param path ==> 路由菜单访问路径
 * @param name ==> 路由 name (对应页面组件 name, 可用作 KeepAlive 缓存标识 && 按钮权限筛选)
 * @param redirect ==> 路由重定向地址
 * @param component ==> 视图文件路径
 * @param meta ==> 路由菜单元信息
 * @param meta.icon ==> 菜单和面包屑对应的图标
 * @param meta.title ==> 路由标题 (用作 document.title || 菜单的名称)
 * @param meta.activeMenu ==> 当前路由为详情页时，需要高亮的菜单
 * @param meta.isLink ==> 路由外链时填写的访问地址
 * @param meta.isHide ==> 是否在菜单中隐藏 (通常列表详情页需要隐藏)
 * @param meta.isFull ==> 菜单是否全屏 (示例：数据大屏页面)
 * @param meta.isAffix ==> 菜单是否固定在标签页中 (首页通常是固定项)
 * @param meta.isKeepAlive ==> 当前路由是否缓存
 * @param meta.hideMenu ==> 当前路由是否显示左侧菜单
 * @param meta.hideTabs ==> 当前路由是否显示顶部页签，true时隐藏页签
 * @param meta.hideHeader ==> 当前路由是否显示Header，true时隐藏
 * */
// 权限路由
export const syncRouters = [
    ...myTaskRouter,
    ...purchaseSalesInventoryRouters,
    ...warehouseSupervisionRouters,
    ...warehouseManagementRouters,
    ...dataAnalysisRouter,
    ...priceMonitorRouter,
    ...aiRiskControlRouter,
    ...lianYunUser,
    ...manageCenterRouter,
    ...messageNotify
];
const router: Router = createRouter({
    history: routerMode,
    routes: [...staticRouter, ...errorRouter, ...authenticationRouter],
    strict: false,
    scrollBehavior: () => ({ left: 0, top: 0 })
});

// 首次加载页面刷新UserInfo
const flushedUserInfo = ref(false);

// 存储已添加的路由名称 systemType
const addedRoutes = new Set<number>();
// 根据角色动态加入路由
export const addRoleRoutes = async (role: number) => {
    if (addedRoutes.has(role)) return;
    try {
        const userStore = useUserStore();
        const processRoute = (route: RouteRecordRaw) => {
            // 处理 meta.title
            if (route.meta && typeof route.meta.title === "function") {
                route.meta.title = route.meta.title({ userStore });
            }
            // 递归处理子路由
            if (route.children && Array.isArray(route.children)) {
                route.children.forEach(childRoute => processRoute(childRoute));
            }
            return route;
        };
        const roleRoutes = syncRouters.map(route => processRoute({ ...route }));
        roleRoutes.forEach(route => {
            if (!router.hasRoute(route.name as string)) {
                router.addRoute(route);
            }
        });
        addedRoutes.add(role);
    } catch (error) {
        console.error("Failed to add role routes:", error);
    }
};
const CURRENT_ENV = import.meta.env.VITE_USER_ENV || "";
const SASS_LOGIN_URL = import.meta.env?.VITE_LOGIN_APP_URL || "";
/**
 * @description 路由拦截 beforeEach
 * */
router.beforeEach(async (to, from, next) => {
    try {
        const userStore = useUserStore();
        const usePathStore = useUserPath();
        //判断路径中是否有token
        if (to?.query?.token) {
            userStore.setToken(to.query.token as string);
            await getUserInfo();
            return next({ path: HOME_URL, replace: true });
        }
        //判断saas无token-或者saas跳login页进行跳转
        const isLoginPath = to.path.toLowerCase().includes(LOGIN_URL);
        if (CURRENT_ENV === "saas" && SASS_LOGIN_URL && (!userStore.token || isLoginPath)) {
            window.location.replace(SASS_LOGIN_URL);
        }
        //设置页面路径
        usePathStore.setPath(from.path);
        sessionStorage.setItem("toPath", to.fullPath);
        // 2.动态设置标题
        const title = userStore.userInfo?.supplier?.systemName || import.meta.env.VITE_GLOB_APP_TITLE;
        if (typeof to.meta.title === "function") {
            const metaTitle = to.meta.title(to.query);
            document.title = (metaTitle ? `${metaTitle?.trim() ?? ""} - ${title}` : title) as string;
        } else if (title) {
            document.title = (to.meta.title ? `${(to.meta.title as string)?.trim() ?? ""} - ${title}` : title) as string;
        }
        // 3.判断是访问登陆页，有 Token 就在当前页面，没有 Token 重置路由到登陆页
        if (to.path.toLocaleLowerCase().includes(LOGIN_URL)) {
            console.log("访问登陆页");
            resetRouter();
            return next();
        }
        console.log("访问页面", to);

        // 4.判断访问页面是否在路由白名单地址(静态路由)中，如果存在直接放行
        if (ROUTER_WHITE_LIST.includes(to.path)) return next();
        console.log("访问页面2", to);

        // 5.判断是否有 Token，没有重定向到 login 页面
        if (!userStore.token) {
            console.log("没有token,重定向到 login 页面");
            return next({ path: LOGIN_URL, replace: true });
        }
        if (!flushedUserInfo.value) {
            await userStore.refreshUserInfo(false);
            flushedUserInfo.value = true;
        }

        console.log(
            "访问页面3",
            to.meta.authOptions !== undefined && (to.meta.authOptions as { label: string; value: string }[]).length
        );
        // 未完成认证，仅显示认证页路由
        if (to.path.toLocaleLowerCase().includes(AUTHENTICATION_URL.toLocaleLowerCase())) {
            if (userStore.enterpriseAuth) {
                return next({ path: HOME_URL, replace: true });
            }
            return next();
        }
        if (!userStore.enterpriseAuth) {
            // router.addRoute(authenticationRouter);
            console.log(router.getRoutes());
            return next({ path: AUTHENTICATION_URL, replace: true });
        }
        // 根据是否sass平台，进行动态添加路由
        if (!addedRoutes.has(userStore.userInfo.systemType)) {
            await addRoleRoutes(userStore.userInfo.systemType);
            return next(to.fullPath);
        }
        // 6.如果没有菜单列表，就重新请求菜单列表并添加动态路由
        if (to.meta.authOptions !== undefined && (to.meta.authOptions as { label: string; value: string }[]).length) {
            const permissions = userStore.userInfo.permissions || [];
            const list = (to.meta.authOptions as { label: string; value: string }[]).map(elem =>
                permissions.includes(elem.value)
            );
            if (list.includes(true)) {
                return next();
            } else {
                return next({ path: NO_PERMISSION_URL });
            }
        }
    } catch (error) {
        console.log(error);
    }
    next();
});

/**
 * @description 重置路由
 * */
export const resetRouter = () => {
    const authStore = useAuthStore();
    authStore.flatMenuListGet.forEach(route => {
        const { name } = route;
        if (name && router.hasRoute(name)) router.removeRoute(name);
    });
};

/**
 * @description 路由跳转错误
 * */
router.onError(error => {
    console.warn("路由错误", error.message);
});

export default router;
